
import React, { useState, useEffect, useMemo } from 'react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    LineChart, Line, AreaChart, Area
} from 'recharts';
import { fetchVisitStats } from '../../services';
import type { VisitStat } from '../../types';
import { TrendingUpIcon, UsersIcon, UserIcon, ClockIcon } from '../ui/Icons';
import Spinner from '../ui/Spinner';

const SystemStatsPage: React.FC = () => {
    const [stats, setStats] = useState<VisitStat[]>([]);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<'7d' | '30d'>('7d');

    useEffect(() => {
        const loadStats = async () => {
            try {
                const data = await fetchVisitStats();
                setStats(data);
            } catch (err) {
                console.error("Failed to load visit stats:", err);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, []);

    const filteredStats = useMemo(() => {
        if (timeRange === '7d') return stats.slice(-7);
        return stats;
    }, [stats, timeRange]);

    const totals = useMemo(() => {
        return filteredStats.reduce((acc, curr) => ({
            guest: acc.guest + curr.guestCount,
            user: acc.user + curr.userCount,
            total: acc.total + curr.totalCount
        }), { guest: 0, user: 0, total: 0 });
    }, [filteredStats]);

    if (loading) return <div className="h-full flex items-center justify-center"><Spinner /></div>;

    return (
        <div className="p-6 space-y-6 animate-fade-in pb-20">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-800 tracking-tight">Thống kê truy cập</h1>
                    <p className="text-gray-500 text-sm">Theo dõi lưu lượng truy cập website hàng ngày</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-xl">
                    <button 
                        onClick={() => setTimeRange('7d')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${timeRange === '7d' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        7 ngày qua
                    </button>
                    <button 
                        onClick={() => setTimeRange('30d')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${timeRange === '30d' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        30 ngày qua
                    </button>
                </div>
            </header>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="bg-blue-50 p-3 rounded-xl text-blue-600">
                        <TrendingUpIcon size={24} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tổng lượt truy cập</p>
                        <p className="text-2xl font-black text-gray-800">{totals.total.toLocaleString()}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="bg-emerald-50 p-3 rounded-xl text-emerald-600">
                        <UserIcon size={24} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cư dân (Đã đăng nhập)</p>
                        <p className="text-2xl font-black text-gray-800">{totals.user.toLocaleString()}</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                    <div className="bg-orange-50 p-3 rounded-xl text-orange-600">
                        <UsersIcon size={24} />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Khách (Chưa đăng nhập)</p>
                        <p className="text-2xl font-black text-gray-800">{totals.guest.toLocaleString()}</p>
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <TrendingUpIcon className="text-primary" />
                        Biểu đồ xu hướng truy cập
                    </h3>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={filteredStats}>
                                <defs>
                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis 
                                    dataKey="date" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    tickFormatter={(val) => val.split('-').slice(1).reverse().join('/')}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                                />
                                <Area type="monotone" dataKey="totalCount" name="Tổng cộng" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorTotal)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <UsersIcon className="text-primary" />
                        Phân loại người dùng
                    </h3>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={filteredStats}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis 
                                    dataKey="date" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    tickFormatter={(val) => val.split('-').slice(1).reverse().join('/')}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                                />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '12px', fontWeight: 'bold' }} />
                                <Bar dataKey="userCount" name="Cư dân" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="guestCount" name="Khách" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Detailed Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <ClockIcon className="text-primary" />
                        Chi tiết truy cập theo ngày
                    </h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Ngày</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Cư dân</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Khách</th>
                                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Tổng cộng</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {[...filteredStats].reverse().map((s) => (
                                <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4 text-sm font-bold text-gray-700">
                                        {s.date.split('-').reverse().join('/')}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-emerald-600 font-bold text-right">
                                        {s.userCount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-orange-600 font-bold text-right">
                                        {s.guestCount.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-blue-600 font-black text-right">
                                        {s.totalCount.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SystemStatsPage;
